/**
 * Door to door mode, in Location under the two ends: trucking between each
 * end's address and the airport or port of an air or ocean search. Part of
 * LaneSearchForm; the trucking cargo is the LTL form of the Road section
 * (`RoadCargoSection ltlOnly`), or the Road shipment itself when Road is ticked.
 */
import React, { useId } from 'react';
import { Form } from 'antd';

import { ToggleSwitch } from '../../../components';
import type { LaneMode } from '../../../apis/lanePlanner';
import { LaneFormValues, roadTruckingProblem } from './laneForm';
import { truckingProblem } from './placeValue';
import styles from './styles.module.css';

const SwitchField: React.FC<{
  id: string;
  value?: boolean;
  onChange?: (checked: boolean) => void;
  disabled: boolean;
}> = ({ id, value, onChange, disabled }) => (
  <ToggleSwitch
    alignLeft
    id={id}
    checked={!!value && !disabled}
    disabled={disabled}
    onChange={(event) => onChange?.(event.target.checked)}
  />
);

const DoorToDoorSwitch: React.FC<{ modes: LaneMode[] }> = ({ modes }) => {
  const form = Form.useFormInstance<LaneFormValues>();
  const id = useId();
  const origin = Form.useWatch('origin', form);
  const destination = Form.useWatch('destination', form);
  const roadType = Form.useWatch('roadType', form);
  const on = !!Form.useWatch('doorToDoor', form);
  const roadProblem = roadTruckingProblem(modes, roadType);
  // An end that cannot be trucked is left out of the search, not unticked: say why.
  const endProblems = (
    [
      ['Origin', origin],
      ['Destination', destination],
    ] as const
  ).flatMap(([end, place]) => {
    const problem = place ? truckingProblem(place, modes) : null;
    return problem ? [`${end}: ${problem}`] : [];
  });
  const reasons = roadProblem ? [roadProblem] : on ? endProblems : [];

  return (
    <div className={styles.doorSwitch}>
      <div className={styles.doorSwitchRow}>
        <Form.Item name='doorToDoor' noStyle>
          <SwitchField id={id} disabled={!!roadProblem} />
        </Form.Item>
        <label htmlFor={id} className={styles.doorSwitchLabel}>
          Door to door mode
        </label>
      </div>
      <div className={styles.doorSwitchHint}>
        Add road legs to and from your addresses.
      </div>
      {reasons.map((reason) => (
        <div key={reason} className={styles.doorSwitchHint}>
          {reason}
        </div>
      ))}
    </div>
  );
};

export default DoorToDoorSwitch;
